// ---------------------------------------------------------------
// App.js — Puerta de entrada. Tras el login, todo vive dentro del
// Portal de gestión (PortalLayout) con secciones: Dashboard,
// Calendario y Horario para todos; Usuarios, Importar Excel y
// Config. horas solo para administradores. Cada usuario tiene una
// ficha en `users` (se crea sola en el primer inicio de sesión).
// ---------------------------------------------------------------
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "./firebase";
import {
  ensureUserProfile,
  touchLastSeen,
  sincronizarDirectorio,
} from "./services/userService";
import { hasPasswordProvider } from "./services/authService";
import { useTheme } from "./hooks/useTheme";
import AuthView from "./components/AuthView";
import SetPasswordModal from "./components/SetPasswordModal";
import PortalLayout from "./components/portal/PortalLayout";
import PauseScreen, { isFreeDay } from "./components/PauseScreen";
import { SectionSkeleton } from "./components/Skeleton";
import { useSchedule } from "./hooks/useSchedule";
import { monthKey } from "./services/scheduleService";
import { toISO } from "./utils/date";
import { SectionContextProvider } from "./hooks/useSectionContext";
import PrivacyGate from "./components/PrivacyGate";
import SinAcceso from "./components/SinAcceso";
import ErrorBoundary from "./components/ErrorBoundary";
import PasswordNudge, {
  tocaAvisarPassword,
} from "./components/PasswordNudge";

// Cada sección se descarga la primera vez que se abre (y queda en
// caché): el arranque de la web es mucho más ligero. No cambia nada
// del funcionamiento; sólo se reparte la descarga.
const DashboardSection = lazy(() => import("./components/sections/DashboardSection"));
const CalendarApp = lazy(() => import("./components/CalendarApp"));
const ScheduleSection = lazy(() => import("./components/sections/ScheduleSection"));
const TimeclockSection = lazy(() => import("./components/sections/TimeclockSection"));
const UsersSection = lazy(() => import("./components/sections/UsersSection"));
const ImportSection = lazy(() => import("./components/sections/ImportSection"));
const HoursConfigSection = lazy(() => import("./components/sections/HoursConfigSection"));
const GuideSection = lazy(() => import("./components/sections/GuideSection"));
const TasksSection = lazy(() => import("./components/sections/TasksSection"));
const ProfileSection = lazy(() => import("./components/sections/ProfileSection"));
// Estas dos se importaban de la forma normal, así que viajaban en el
// paquete principal: las descargaba TODO el mundo, incluidos los
// empleados, que ni siquiera pueden entrar en ellas.
const NotesSection = lazy(() => import("./components/sections/NotesSection"));
const LogsSection = lazy(() => import("./components/sections/LogsSection"));

/** Silueta del contenido mientras llega la sección */
const SectionLoader = () => <SectionSkeleton />;

const ADMIN_SECTIONS = new Set(["users", "import", "hoursconfig", "notes", "logs"]);
const ALL_SECTIONS = new Set([
  "dashboard", "calendar", "schedule", "timeclock", "tasks", "guide",
  "profile", "users", "import", "hoursconfig", "notes", "logs",
]);
const PAUSE_KEY = "pause-ok"; // guarda el día en que pulsó "Sí, continuar"
const SECTION_KEY = "portal-section";

export default function App() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null); // ficha en `users`
  const [checking, setChecking] = useState(true);
  // Se recuerda la sección activa para que al recargar no vuelva
  // siempre al Dashboard
  const [section, setSectionRaw] = useState(() => {
    const saved = localStorage.getItem(SECTION_KEY);
    return ALL_SECTIONS.has(saved) ? saved : "dashboard";
  });
  const setSection = (s) => {
    setSectionRaw(s);
    localStorage.setItem(SECTION_KEY, s);
    // Al navegar se olvida la tarea resaltada: si se vuelve a
    // Tareas más tarde, se abre en el día de hoy y sin resaltado.
    setTaskFocus(null);
  };
  const [searchTerm, setSearchTerm] = useState("");
  // Tarea a resaltar al llegar desde el calendario: {taskId, date, at}
  const [taskFocus, setTaskFocus] = useState(null);
  const [showSetPassword, setShowSetPassword] = useState(false);
  const [sinAcceso, setSinAcceso] = useState(false);
  const [avisoPassword, setAvisoPassword] = useState(false);

  /* El aviso se decide UNA vez, al llegar la ficha, y no en cada
     repintado: si dependiera de `profile` en vivo, al guardar la
     respuesta ("no volver a avisar") el modal parpadearía antes de
     desaparecer.

     OJO CON EL TEMPORIZADOR: `profile` viene de un onSnapshot y
     cambia de identidad nada más entrar, porque `touchLastSeen`
     escribe en la propia ficha. Si el temporizador se cancelase en la
     limpieza del efecto, React lo mataría en ese repintado y el aviso
     no saldría NUNCA. Por eso vive en una referencia y sólo se
     cancela al salir de la aplicación. */
  const avisoDecidido = useRef(false);
  const avisoTemporizador = useRef(null);
  useEffect(() => () => clearTimeout(avisoTemporizador.current), []);
  useEffect(() => {
    if (avisoDecidido.current || !profile || showSetPassword) return;
    avisoDecidido.current = true;
    if (tocaAvisarPassword(profile)) {
      // Un respiro tras entrar: recibir un modal en la misma cara con
      // la que se abre la web es desagradable.
      avisoTemporizador.current = setTimeout(
        () => setAvisoPassword(true),
        1200
      );
    }
  }, [profile, showSetPassword]);
  const [theme, toggleTheme] = useTheme();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setChecking(false);
    });
    return unsubscribe;
  }, []);

  // Ficha del usuario en Firestore (rol, username). Ya NO se crea sola:
  // sólo si la dirección dejó una invitación para ese correo. Sin ficha,
  // o dado de baja, se enseña "sin acceso" (ver userService.js).
  useEffect(() => {
    if (!user) {
      setProfile(null);
      setSinAcceso(false);
      return undefined;
    }
    ensureUserProfile(user)
      .then((r) => setSinAcceso(r === "sin-acceso"))
      .catch((err) => {
        console.error(err);
        setSinAcceso(true);
      });
    // Registrar la última entrada (para la gestión de usuarios)
    touchLastSeen(user.uid).catch(() => {});
    const unsubscribe = onSnapshot(
      doc(db, "users", user.uid),
      (snap) => {
        if (!snap.exists()) return;
        const datos = snap.data();
        // Si la dan de baja con la web abierta, sale en ese momento
        if (datos.activo === false) setSinAcceso(true);
        setProfile(datos);
      },
      (error) => {
        console.error("Error leyendo perfil de usuario:", error);
      }
    );
    return unsubscribe;
  }, [user]);

  const isAdmin = profile?.role === "admin";

  // Un administrador, al entrar, deja el directorio del equipo al día
  // (ver sincronizarDirectorio). Una vez por sesión.
  const directorioHecho = useRef(false);
  useEffect(() => {
    if (!isAdmin || directorioHecho.current) return;
    directorioHecho.current = true;
    sincronizarDirectorio().catch((err) =>
      console.error("No se pudo sincronizar el directorio:", err)
    );
  }, [isAdmin]);

  // -------- Pantalla de pausa en días libres (no admins) --------
  // Se consulta el horario del mes: si hoy no hay turno (descanso,
  // vacaciones o descanso post-guardia) se invita a desconectar.
  const now = new Date();
  const todayIso = toISO(now);
  const { schedule: mySchedule, loading: myScheduleLoading } = useSchedule(
    user && profile && !isAdmin ? user.uid : null,
    monthKey(now.getFullYear(), now.getMonth() + 1)
  );
  const [pauseOk, setPauseOk] = useState(
    () => localStorage.getItem(PAUSE_KEY) === toISO(new Date())
  );

  useEffect(() => {
    if (user && profile && !hasPasswordProvider(user)) {
      setShowSetPassword(true);
    } else {
      setShowSetPassword(false);
    }
  }, [user, profile]);

  // Un no-admin nunca se queda en una sección de administración
  useEffect(() => {
    if (profile && !isAdmin && ADMIN_SECTIONS.has(section)) {
      setSection("dashboard");
    }
  }, [profile, isAdmin, section]);

  if (user && sinAcceso) {
    return <SinAcceso email={user.email} />;
  }

  if (checking || (user && !profile)) {
    return (
      <div className="app-loading">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) return <AuthView />;

  // Día libre → pantalla de pausa (salvo que ya pulsara "continuar" hoy)
  const todayInfo = mySchedule?.days?.[String(now.getDate())];
  if (!isAdmin && !myScheduleLoading && isFreeDay(todayInfo) && !pauseOk) {
    return (
      <PauseScreen
        uid={user.uid}
        displayName={profile?.username || profile?.displayName}
        schedule={mySchedule}
        onContinue={() => {
          localStorage.setItem(PAUSE_KEY, todayIso);
          setPauseOk(true);
        }}
      />
    );
  }

  return (
    <>
      <SectionContextProvider>
        <PrivacyGate user={user} profile={profile}>
    <PortalLayout
        user={user}
        profile={profile}
        isAdmin={isAdmin}
        section={section}
        onSection={(s) => {
          setSection(s);
          setSearchTerm("");
        }}
        theme={theme}
        toggleTheme={toggleTheme}
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
      >
        {/* La red de seguridad envuelve la sección visible, y se
            reinicia al cambiar de apartado. Si algo revienta, se cae
            SÓLO esta parte: el menú, el fichaje y el resto siguen. */}
        <ErrorBoundary seccion={section}>
        <Suspense fallback={<SectionLoader />}>
          {section === "dashboard" && (
            <DashboardSection
              user={user}
              profile={profile}
              isAdmin={isAdmin}
              onNavigate={setSection}
            />
          )}
          {section === "calendar" && (
            <CalendarApp
              user={user}
              onOpenTasks={(taskId, date) => {
                // Primero navegar (limpia el foco anterior) y luego
                // marcar la tarea a resaltar en esta visita
                setSection("tasks");
                if (taskId) setTaskFocus({ taskId, date, at: Date.now() });
              }}
            />
          )}
          {section === "schedule" && (
            <ScheduleSection user={user} profile={profile} isAdmin={isAdmin} />
          )}
          {section === "timeclock" && (
            <TimeclockSection
              user={user}
              profile={profile}
              isAdmin={isAdmin}
              onNavigate={setSection}
            />
          )}
          {section === "tasks" && (
            <TasksSection
              user={user}
              profile={profile}
              isAdmin={isAdmin}
              focus={taskFocus}
            />
          )}
          {section === "guide" && <GuideSection isAdmin={isAdmin} />}
          {section === "profile" && (
            <ProfileSection user={user} profile={profile} isAdmin={isAdmin} />
          )}
          {isAdmin && section === "users" && (
            <UsersSection user={user} searchTerm={searchTerm} />
          )}
          {isAdmin && section === "import" && <ImportSection />}
          {isAdmin && section === "hoursconfig" && <HoursConfigSection />}
          {isAdmin && section === "notes" && <NotesSection user={user} />}
          {isAdmin && section === "logs" && <LogsSection />}
        </Suspense>
        </ErrorBoundary>
      </PortalLayout>
        </PrivacyGate>
    </SectionContextProvider>

      {showSetPassword && user && (
        <SetPasswordModal
          user={user}
          onDone={() => setShowSetPassword(false)}
          onSkip={() => setShowSetPassword(false)}
        />
      )}

      {/* Recordatorio de cambiar la contraseña, para las cuentas que
          se crearon cuando la elegía el administrador. Va DESPUÉS del
          modal de establecer contraseña para no encadenar dos
          ventanas, y sólo cuando la persona ya está dentro. */}
      {avisoPassword && user && profile && (
        <PasswordNudge
          user={user}
          profile={profile}
          onClose={() => setAvisoPassword(false)}
        />
      )}
    </>
  );
}
